import { useMemo } from 'react';
import { Form } from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/models.pb';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { DeviceSize } from '@frontend/device-size-field';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  BaseFieldProps,
  DynamicFieldAttributes,
  SharedDynamicFieldsProps,
  SpinningLoader,
  useFormField,
} from '@frontend/design-system';
import { ContentComposer } from '../content-composer/content-composer';
import type { ComposerMode, ComposerParts } from '../content-composer/types';
import { isValidSchemaSection } from '../content-composer/utils/is-valid-schema-section';
import { ButtonSection } from '../parts/button-section/button-section';
import { CustomCodeSection } from '../parts/custom-code-section/custom-code-section';
import { EmailAssistant } from '../parts/email-assistant/email-assistant';
import { FooterSection } from '../parts/footer-section/footer-section';
import { ImageSection } from '../parts/image-section/image-section';
import { LogoSection } from '../parts/logo-section/logo-section';
import { TextSection } from '../parts/text-section/text-section';
import { EmailFooter } from './email-footer/email-footer';
import { EmailHeader } from './email-header/email-header';
import { OnSaveChanges, OnTemplateNameChange } from './types';
import { emailComposerMigrateLegacyToSchemaType } from './utils/email-composer-migrate-legacy-to-schema-type';

export const EMAIL_COMPOSER_PARTS: ComposerParts = {
  EmailAssistant,
  LogoSection,
  TextSection,
  ImageSection,
  ButtonSection,
  CustomCodeSection,
  FooterSection,
};

type Props = {
  className?: string;
  composerMode: ComposerMode;
  createTemplateAccess: boolean;
  disabledBack?: boolean;
  editTemplateAccess: boolean;
  form?: Form;
  isLoading?: boolean;
  locationIds: string[];
  onBack: () => void;
  onSave: (data: OnSaveChanges, applyChanges: boolean) => void;
  onTemplateNameSave: (data: OnTemplateNameChange) => void;
  setForm?: (form: Form) => void;
  sharedDynamicFieldProps?: SharedDynamicFieldsProps;
  subject: string;
  subjectDynamicFields?: DynamicFieldAttributes[];
  templateJson: string;
  templateTitle?: string;
  templateTypeSlug: TemplateType_Slug;
  textDynamicFields?: DynamicFieldAttributes[];
  trackingPrefix: string;
};

// TODO: Improve the props documentation here. Also add documentation to ContentComposer.
// @params createTemplateAccess - boolean to determine if the user has access to create a new template
// @params editTemplateAccess - boolean to determine if the user has access to edit the template
// @params isLoading - boolean to determine if the template is still loading
// @params locationIds - array of selected location ids from the form
// @params onBack - callback to handle the back button click
// @params onSave - callback function that fires when the user clicks continue
// @params onTemplateNameSave - callback function that fires when the user clicks confirm & save template
// @params subject - email subject
// @params templateJson - stringified JSON of the template which will show in the content composer
// @params templateTitle - template title (derived from the fetched template data)

export const EmailComposer = ({
  className,
  composerMode,
  createTemplateAccess,
  disabledBack = false,
  editTemplateAccess,
  form,
  isLoading,
  locationIds,
  onBack,
  onSave,
  onTemplateNameSave,
  setForm,
  sharedDynamicFieldProps,
  subject,
  subjectDynamicFields,
  templateJson,
  templateTitle,
  templateTypeSlug,
  textDynamicFields,
  trackingPrefix,
}: Props) => {
  const { t } = useTranslation('email-composer');

  const subjectProps = useFormField({ type: 'text', value: subject }, [subject]);
  const deviceSizeProps = useFormField({ type: 'optionswitch', value: 'desktop' }) as BaseFieldProps<DeviceSize>;

  const formattedTemplateJson = useMemo(() => {
    if (!templateJson) return undefined;
    const parsedTemplateJson = JSON.parse(templateJson);
    const firstSection = parsedTemplateJson?.sections?.[0];

    // Migrate legacy composer documents
    if (!isValidSchemaSection(firstSection))
      return emailComposerMigrateLegacyToSchemaType(parsedTemplateJson).formattedSections;

    return parsedTemplateJson;
  }, [templateJson]);

  return isLoading ? (
    <div css={{ display: 'flex', justifyContent: 'center', marginTop: theme.spacing(5), width: '100%' }}>
      <SpinningLoader />
    </div>
  ) : (
    <ContentComposer
      composerMode={composerMode}
      className={className}
      css={
        // TODO: Move desktop and mobile width styles into a constants file
        deviceSizeProps.value === 'mobile' && {
          '.composer-section': {
            maxWidth: '375px',
          },
        }
      }
      form={form}
      initialValue={formattedTemplateJson}
      locationIds={locationIds}
      parts={EMAIL_COMPOSER_PARTS}
      setForm={setForm}
      sharedDynamicFieldProps={sharedDynamicFieldProps}
      subjectDynamicFields={subjectDynamicFields}
      templateTypeSlug={templateTypeSlug}
      textDynamicFields={textDynamicFields}
      trackingIdPrefix={trackingPrefix}
    >
      <EmailHeader deviceSizeProps={deviceSizeProps} subjectProps={subjectProps} trackingPrefix={trackingPrefix} />
      <ContentComposer.Body
        emptyStateTitle={t('Design Your Email Here')}
        emptyStateSubTitle={t('Select or drag & drop options from the toolbar to build your email.')}
      >
        <ContentComposer.Body.LocationPicker />
      </ContentComposer.Body>
      <EmailFooter
        createTemplateAccess={createTemplateAccess}
        disabledBack={disabledBack}
        editTemplateAccess={editTemplateAccess}
        onBack={onBack}
        onSave={onSave}
        onTemplateNameSave={onTemplateNameSave}
        subject={subjectProps.value}
        templateTitle={templateTitle}
        trackingPrefix={trackingPrefix}
      />
    </ContentComposer>
  );
};
